import { toCamelCase } from '@agilepoint/utils-common';

interface ICommonTextDataArgs {
    mobileAppName: string | null;
    mobileAppVersion: string | null;
    mobileAppDescription: string | null;
    companyName: string | null;
    companyInfoEmail: string | null;
    companySupportEmail: string | null;
    companyWebsite: string | null;
    copyRightInfo: string | null;
    defaultLocale: string | null;
}

interface IAppXMLData extends ICommonTextDataArgs {
    features: {
        name: string | null;
        enabled: string | null;
        displayName: string | null;
        isLocalized: string | null;
    }[];
    styles: {
        themeName: string | null;
        menuBackgroundColor: string | null;
        buttonColor: string | null;
    };
    mobileAppPlatforms: {
        ios: boolean;
        android: boolean;
    };
    appFilters: string;
    isNewApp: boolean;
}

const dom = new DOMParser();

const getFeatures = (args: HTMLCollection) => {
    const features = [];
    for (const element of args) {
        if (element.getAttribute('Enabled') === 'true') {
            features.push({
                name: element?.getAttribute('Name'),
                enabled: element?.getAttribute('Enabled'),
                displayName: element?.getAttribute('DisplayName'),
                isLocalized: element?.getAttribute('IsLocalized')
            });
        }
    }
    return features;
};

const getStyles = (appTheme: Element) => {
    const themeName = appTheme.getAttribute('ThemeName');
    const menuBackgroundColor = appTheme.getAttribute('MenuBackgroundColor');
    const buttonColor = appTheme.getAttribute('ButtonColor');
    return { themeName, menuBackgroundColor, buttonColor };
};

const getMobileAppPlatforms = (mobileApp: Element) => {
    const ios = mobileApp.getAttribute('IOS');
    const android = mobileApp.getAttribute('Android');
    return { ios: ios === 'true', android: android === 'true' };
};

const getFilters = (appFilters: Element) => {
    const foundAppFilters = [];
    const appFilterNodes = (appFilters.getElementsByTagName('ApplicationFilters')[0]);
    for (const element of appFilterNodes.children) {
        foundAppFilters.push(element.textContent);
    }

    return foundAppFilters.join(', ');
};

const checkForNewApp = (xml: Document) => {
    const res = xml.getElementsByTagName('AuthenticationProviders')[0].getElementsByTagName('AuthProvider');

    const arr = [];
    for (const value of res) {
        const nameAttributeValue = (value?.attributes?.getNamedItem('Name')!.nodeValue);
        arr.push(nameAttributeValue);
    }

    return arr.includes('PingFederate') || arr.includes('AmazonCognito');
};

const xmlTagNames = ['MobileAppName', 'MobileAppVersion', 'MobileAppDescription', 'CompanyName',
    'CompanyWebsite', 'CompanySupportEmail', 'CompanyInfoEmail', 'CopyRightInfo', 'DefaultLocale'];

export const getAppDataFromXML = (xmlStringContent: string): IAppXMLData => {
    const xml = dom.parseFromString(xmlStringContent, 'text/xml');
    const getTagValue = (tagName: string) => xml.getElementsByTagName(tagName)[0].textContent;
    const commonTextData = xmlTagNames.reduce((accumulator, xmlTag) => {
        const tagValue = getTagValue(xmlTag);
        accumulator[toCamelCase(xmlTag)] = tagValue;
        return accumulator;
    }, {} as ICommonTextDataArgs);

    const features = getFeatures(xml.getElementsByTagName('MobileAppMenu')[0].children);

    //We are directly reading ApplicationTheme tag attribute value as all the platform values are same (IOS, Android, Windows).
    const styles = getStyles(xml.getElementsByTagName('ApplicationTheme')[0]);

    const appFilters = getFilters(xml.getElementsByTagName('MobileAppCommonSettings')[0]);

    const mobileAppPlatforms = getMobileAppPlatforms(xml.getElementsByTagName('MobileAppPlatform')[0]);

    return {
        ...commonTextData,
        features,
        styles: styles,
        mobileAppPlatforms: mobileAppPlatforms,
        appFilters,
        isNewApp: checkForNewApp(xml)
    };
};
